import StatusDot, {
  StatusStyle,
} from '../../../../components/Status/StatusDot';
import { constantToLabel } from '@ecosconnect/core/src/helpers/string';
import React from 'react';
import { Flex } from 'rebass';
import { Column } from '../../../../hooks/useTablePages';
import StatusChip from '../../../../components/Status/StatusChip';
import {
  hazardProtectionStatusStyle,
  hazardProtectionStatusOptions,
} from '../../../../components/WaterService/HazardProtectionStatusPicker';
import Icon from '../../../../components/Icon/Icon';

const hazardColumns: Column[] = [
  {
    header: 'Hazard Site',
    accessor: 'hazardSite.name',
    width: 120,
  },
  {
    header: 'Hazard Status',
    id: 'hazardProtectionStatus',
    accessor: (row: any) => {
      if (!row.hazardProtectionStatus) return undefined;
      const opt = hazardProtectionStatusOptions.find(
        (o) => o.value === row.hazardProtectionStatus
      );
      return (
        <StatusChip
          status={hazardProtectionStatusStyle(row.hazardProtectionStatus)}
        >
          {opt?.label ?? constantToLabel(row.hazardProtectionStatus)}
        </StatusChip>
      );
    },
    stringAccessor: (row: any): string =>
      hazardProtectionStatusOptions.find(
        (o) => o.value === row.hazardProtectionStatus
      )?.label ?? constantToLabel(row.hazardProtectionStatus),
    width: 140,
  },
];

const baseColumns: Column[] = [
  {
    header: 'Status',
    id: 'status',
    accessor: (waterService: any) => {
      let style: StatusStyle = 'default';
      switch (waterService.status) {
        case 'ACTIVE':
          style = 'success';
          break;
        case 'INACTIVE':
          style = 'error';
          break;
      }
      return <StatusDot status={style} mr="1" />;
    },
    stringAccessor: (waterService: any): string => {
      return constantToLabel(waterService.status);
    },
    width: 80,
  },
  {
    header: 'Service',
    accessor: 'siteNumber',
    width: 120,
  },
  {
    header: 'Tags',
    id: 'tags.text',
    accessor: (waterService: any) => {
      return (
        <Flex sx={{ flexWrap: 'nowrap', gap: 1 }}>
          {(waterService.tags ?? []).map((tag: string) => (
            <StatusChip status="default" key={tag}>
              {tag}
            </StatusChip>
          ))}
        </Flex>
      );
    },
    stringAccessor: (waterService: any): string => {
      return (waterService.tags ?? []).join(', ');
    },
    width: 150,
  },
  {
    header: 'Meter',
    accessor: 'meterNumber',
    width: 150,
  },
  {
    header: 'TRAC',
    accessor: 'pin',
    width: 100,
  },
  {
    header: 'RW Site',
    accessor: 'recycledWaterSite.name',
    width: 120,
  },
  {
    header: 'Has Assemblies',
    id: 'hasBackflowAssemblies',
    accessor: (row: any) =>
      row.hasBackflowAssemblies ? <Icon icon="check" /> : undefined,
    stringAccessor: (row: any): string =>
      row.hasBackflowAssemblies ? 'Yes' : 'No',
    width: 120,
  },
  {
    header: 'Type',
    id: 'type',
    accessor: (row: any) => constantToLabel(row.type),
    width: 150,
  },
  {
    header: 'Address',
    id: 'address.street1',
    accessor: (row: any) =>
      row.address.street1 +
      (row.address.street2 ? ', ' + row.address.street2 : ''),
    width: 250,
  },
  {
    header: 'City',
    accessor: 'address.city',
    width: 120,
  },
  {
    header: 'State',
    accessor: 'address.state',
    width: 80,
  },
  {
    header: 'Zip',
    id: 'address.zip',
    accessor: (row: any) =>
      row.address.zip + (row.address.zipExt ? '-' + row.address.zipExt : ''),
    width: 120,
  },
];

const getColumns = (hasWaterHazards: boolean): Column[] => {
  if (hasWaterHazards) {
    // Insert hazard columns after 'RW Site' (index 5) and before 'Type'
    return [
      ...baseColumns.slice(0, 6),
      ...hazardColumns,
      ...baseColumns.slice(6),
    ];
  }
  return baseColumns;
};

export default getColumns;
